import { keepPreviousData, useQueryClient } from "@tanstack/react-query";

import { Optional, WithStringValues } from "common/utils/types";
import { useFetchMutation, useFetchQuery } from "common/queries/hooks";

import { ProductListFilterSurface, ProductListSource } from "client/analytics/types";
import { ProductListContext } from "client/analytics/utils";
import { useBreakpoints } from "client/common/hooks";

import {
  carriedDefinitions,
  clearedFilterValues,
  hasAnySelectedFilterValue,
  filtersWithOptions,
} from "../utils";
import {
  BrowseSearch,
  BrowseAllPatientKind,
  IProductLineDetails,
  IProductQueryResponse,
  ProductLineSearch,
  ProductLineListSearch,
  ProductDealsSearch,
  ProductDeals,
  ProductSearchSearch,
  CategoryProductSearch,
  ProductSearch,
  IClientBrowse,
  CarouselDisplay,
  IncentiveDetails,
  ProductListSearch,
  RebateDetails,
  ClientCategory,
  PromotionItems,
  ProductLineAvailabilitySearch,
  ProductLineAvailabilityResponse,
  ItemPricingSearch,
  ItemPricingResponse,
  FilterDefinition,
  FilterValue,
  RecentlyViewedProductLineCreate,
} from "../types";
import { keys, urls } from "./constants";
import { useFilterChangeFrom } from "./utils";

export const useProductLineQuery = (id: number) => {
  return useFetchQuery<IProductLineDetails>(keys.productLineDetail(id), {
    fetch: { url: urls.productLine(id) },
    config: { enabled: !!id },
  });
};

export const useProductQuery = (id: number, { enabled = true }: { enabled?: boolean } = {}) => {
  return useFetchQuery<IProductQueryResponse>(keys.productDetail(id), {
    fetch: { url: urls.product(id) },
    config: { enabled: enabled && !!id },
  });
};

export const useProductLineSearchQuery = (
  search: ProductLineListSearch,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  return useFetchQuery<ProductLineSearch, WithStringValues<ProductLineListSearch>>(
    keys.productLineSearch(search),
    {
      fetch: { url: urls.productLineSearch(), body: search },
      config: {
        enabled,
        placeholderData: keepPreviousData,
      },
    },
  );
};

export const useProductSearchQuery = (
  search: WithStringValues<ProductSearchSearch>,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  return useFetchQuery<ProductSearch, WithStringValues<ProductSearchSearch>>(
    keys.productSearch(search),
    {
      fetch: {
        url: urls.productSearch(),
        body: search,
      },
      config: {
        enabled,
        placeholderData: keepPreviousData,
      },
    },
  );
};

export const useProductDealsSearchQuery = (
  search: WithStringValues<ProductDealsSearch>,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  return useFetchQuery<ProductDeals, WithStringValues<ProductDealsSearch>>(
    keys.productDealsSearch(search),
    {
      fetch: { url: urls.productDealsSearch(), body: search },
      config: {
        enabled,
        placeholderData: keepPreviousData,
      },
    },
  );
};

export const useRebateItemsQuery = (id: number, search: ProductListSearch) => {
  return useFetchQuery<PromotionItems, WithStringValues<ProductListSearch>>(
    keys.rebateItemList(id, search),
    {
      fetch: { url: urls.rebateItems(id), body: search },
      config: {
        placeholderData: keepPreviousData,
      },
    },
  );
};

export const useIncentiveItemsQuery = (
  id: number,
  search: ProductListSearch,
  { enabled = !!id }: { enabled?: boolean } = {},
) => {
  return useFetchQuery<PromotionItems, WithStringValues<ProductListSearch>>(
    keys.incentiveItemList(id, search),
    {
      fetch: { url: urls.incentiveItems(id), body: search },
      config: {
        enabled,
        placeholderData: keepPreviousData,
      },
    },
  );
};

export const useBrowseQuery = (
  search: WithStringValues<BrowseSearch> = {
    species: BrowseAllPatientKind.ALL,
  },
  { enabled }: { enabled: boolean },
) => {
  return useFetchQuery<IClientBrowse, WithStringValues<BrowseSearch>>(keys.browseFilter(search), {
    fetch: { url: urls.browse(), body: search },
    config: { placeholderData: keepPreviousData, enabled },
  });
};

export const useCarouselDisplayQuery = (
  search: WithStringValues<BrowseSearch> = {
    species: BrowseAllPatientKind.ALL,
  },
  { enabled }: { enabled: boolean },
) => {
  return useFetchQuery<CarouselDisplay, WithStringValues<BrowseSearch>>(
    keys.carouselsFilter(search),
    {
      fetch: { url: urls.carousels(), body: search },
      config: { placeholderData: keepPreviousData, enabled },
    },
  );
};

export const useRecentlyViewedProductLineCreateMutation = () => {
  const queryClient = useQueryClient();
  return useFetchMutation<RecentlyViewedProductLineCreate, void>({
    fetch: (body) => ({ method: "POST", url: urls.recentlyViewedProductLines(), body }),
    config: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: keys.browse() });
        queryClient.invalidateQueries({ queryKey: keys.carousels() });
      },
    },
  });
};

export const useCategoryQuery = (id: number) => {
  return useFetchQuery<ClientCategory>(keys.categoryDetail(id), {
    fetch: { url: urls.category(id) },
  });
};

export const useCategoryProductsQuery = (
  id: number,
  search: WithStringValues<ProductListSearch>,
) => {
  const queryKey = keys.categoryProductList(id, search);
  return useFetchQuery<CategoryProductSearch, WithStringValues<ProductListSearch>>(queryKey, {
    fetch: { url: urls.categoryProducts(id), body: search },
    config: {
      enabled: !!id,
      // Keeps the previous results while the shopper filters, but not across categories.
      placeholderData: (previous, previousQuery) =>
        previousQuery?.queryKey[queryKey.length - 2] === id ? previous : undefined,
    },
  });
};

export const useIncentiveQuery = (
  id: number = 0,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  return useFetchQuery<IncentiveDetails>(keys.incentiveDetail(id), {
    fetch: { url: urls.incentive(id) },
    config: { enabled: !!id && enabled },
  });
};

export const useRebateQuery = (id: number, { enabled = true }: { enabled?: boolean } = {}) => {
  return useFetchQuery<RebateDetails>(keys.rebateDetail(id), {
    fetch: { url: urls.rebate(id) },
    config: { enabled: !!id && enabled },
  });
};

export const useIncentiveProductsQuery = (
  id: number,
  search: ProductListSearch,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  return useFetchQuery<ProductSearch, WithStringValues<ProductListSearch>>(
    keys.incentiveProductList(id, search),
    {
      fetch: { url: urls.incentiveProducts(id), body: search },
      config: { enabled: !!id && enabled, placeholderData: keepPreviousData },
    },
  );
};

export const useRebateProductsQuery = (
  id: number,
  search: ProductListSearch,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  return useFetchQuery<ProductSearch, WithStringValues<ProductListSearch>>(
    keys.rebateProductList(id, search),
    {
      fetch: { url: urls.rebateProducts(id), body: search },
      config: { enabled: !!id && enabled, placeholderData: keepPreviousData },
    },
  );
};

export const useProductLineAvailabilityQuery = (
  id: number,
  search: WithStringValues<ProductLineAvailabilitySearch>,
) => {
  return useFetchQuery<
    ProductLineAvailabilityResponse,
    WithStringValues<ProductLineAvailabilitySearch>
  >(keys.productLineDetailAvailability(id, search), {
    fetch: { url: urls.productLineAvailability(id), body: search },
  });
};

/**
 * Query to fetch accurate server-side pricing for add-to-cart button
 * Returns total price including discounts, fees, and shipping with MAP pricing enforcement
 */
export const useItemAddToCartPricing = (
  itemId: number,
  quantity: number,
  isSubscriptionPrice?: boolean,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  return useFetchQuery<ItemPricingResponse, WithStringValues<ItemPricingSearch>>(
    keys.itemPrices(itemId, {
      quantity,
      is_subscription_price: isSubscriptionPrice ?? false,
    }),
    {
      fetch: {
        url: urls.itemAddToCartPricing(itemId),
        body: {
          quantity: quantity.toString(),
          is_subscription_price: isSubscriptionPrice?.toString() ?? "false",
        },
      },
      config: {
        enabled: enabled && !!itemId && quantity > 0,
      },
    },
  );
};

type FilterPanelOptions<TSearch, TFilterOptions> = {
  definitions: FilterDefinition<TSearch, FilterValue, TFilterOptions>[];
  response: Optional<{ filters: TFilterOptions }>;
  filters: TSearch;
  onChange: (next: Partial<TSearch>) => void;
  /** Set false by a surface that hides its filters even when the options would fill a panel. */
  isEnabled?: boolean;
  listSource: ProductListSource;
  listContext?: ProductListContext;
};

/** The state `FilterPanel.Rail` and `FilterPanel.AppliedRow` render from. */
export const useFilterPanel = <TSearch, TFilterOptions>({
  definitions,
  response,
  filters,
  onChange,
  isEnabled = true,
  listSource,
  listContext,
}: FilterPanelOptions<TSearch, TFilterOptions>) => {
  const { isTabletLandscapeAndSmaller } = useBreakpoints();
  const filterOptions = response?.filters;

  const hasFilterOptions =
    !!filterOptions && filtersWithOptions({ definitions, filterOptions }).length > 0;
  const showFilterPanel = isEnabled && !isTabletLandscapeAndSmaller && hasFilterOptions;

  const changeFrom = useFilterChangeFrom({
    definitions,
    filters,
    onChange,
    listSource,
    listContext,
  });

  return {
    filterOptions,
    hasFilterOptions,
    showFilterPanel,
    hasSelectedFilters: hasAnySelectedFilterValue({ definitions, filters, filterOptions }),
    changeFrom,
    clearAllFrom: (surface: ProductListFilterSurface) => () =>
      changeFrom(surface)(
        clearedFilterValues(carriedDefinitions(definitions, filterOptions), filters),
      ),
    // Echoed back so the renderers below need only this one object.
    definitions,
    filters,
  };
};

export type FilterPanelState<TSearch, TFilterOptions> = ReturnType<
  typeof useFilterPanel<TSearch, TFilterOptions>
>;
